import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { SOURCES, WEBSITE_STATES, PRIORITIES } from "@/lib/constants";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Loader2 } from "lucide-react";

const EMPTY = {
  company: "", industry: "", website: "", contact_person: "", phone: "",
  email: "", address: "", district: "", instagram: "", source: "google_maps",
  website_state: "unbekannt", priority: "mittel", problem: "", pitch: "",
  notes: "", potential_value: "",
};

export function NewLeadDialog({ open, onOpenChange, onCreated }) {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const qc = useQueryClient();

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.company.trim()) {
      toast.error("Firmenname ist erforderlich");
      return;
    }
    if (!form.phone.trim() && !form.email.trim()) {
      toast.error("Mindestens Telefon oder E-Mail angeben");
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form, potential_value: parseFloat(form.potential_value) || 0 };
      const res = await api.post("/leads", payload);
      toast.success("Lead angelegt");
      setForm(EMPTY);
      qc.invalidateQueries();
      onOpenChange(false);
      onCreated?.(res.data);
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || "Fehler beim Speichern");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="new-lead-dialog">
        <DialogHeader>
          <DialogTitle className="text-xl">Neuer Lead</DialogTitle>
          <DialogDescription>Pflicht: Firmenname sowie mindestens Telefon oder E-Mail.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2">
              <Label>Firmenname *</Label>
              <Input data-testid="lead-input-company" autoFocus value={form.company}
                onChange={(e) => set("company", e.target.value)} placeholder="z.B. Dachdecker Meyer GmbH" />
            </div>
            <div>
              <Label>Branche</Label>
              <Input data-testid="lead-input-industry" value={form.industry}
                onChange={(e) => set("industry", e.target.value)} placeholder="Handwerk" />
            </div>
            <div>
              <Label>Website</Label>
              <Input data-testid="lead-input-website" value={form.website}
                onChange={(e) => set("website", e.target.value)} placeholder="https://…" />
            </div>
            <div>
              <Label>Ansprechpartner</Label>
              <Input data-testid="lead-input-contact" value={form.contact_person}
                onChange={(e) => set("contact_person", e.target.value)} placeholder="Klaus Meyer" />
            </div>
            <div>
              <Label>Telefonnummer</Label>
              <Input data-testid="lead-input-phone" value={form.phone}
                onChange={(e) => set("phone", e.target.value)} placeholder="+49 …" />
            </div>
            <div>
              <Label>E-Mail</Label>
              <Input data-testid="lead-input-email" type="email" value={form.email}
                onChange={(e) => set("email", e.target.value)} placeholder="info@…" />
            </div>
            <div>
              <Label>Potenzieller Auftragswert (€)</Label>
              <Input data-testid="lead-input-value" type="number" value={form.potential_value}
                onChange={(e) => set("potential_value", e.target.value)} placeholder="3200" />
            </div>
            <div className="sm:col-span-2">
              <Label>Adresse</Label>
              <Input data-testid="lead-input-address" value={form.address}
                onChange={(e) => set("address", e.target.value)} placeholder="Straße, PLZ Ort" />
            </div>
            <div>
              <Label>Bezirk</Label>
              <Input data-testid="lead-input-district" value={form.district}
                onChange={(e) => set("district", e.target.value)} placeholder="z.B. Pankow" />
            </div>
            <div>
              <Label>Instagram</Label>
              <Input data-testid="lead-input-instagram" value={form.instagram}
                onChange={(e) => set("instagram", e.target.value)} placeholder="https://instagram.com/…" />
            </div>
            <div>
              <Label>Priorität</Label>
              <Select value={form.priority} onValueChange={(v) => set("priority", v)}>
                <SelectTrigger data-testid="lead-select-priority"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PRIORITIES.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Quelle</Label>
              <Select value={form.source} onValueChange={(v) => set("source", v)}>
                <SelectTrigger data-testid="lead-select-source"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {SOURCES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Website-Zustand</Label>
              <Select value={form.website_state} onValueChange={(v) => set("website_state", v)}>
                <SelectTrigger data-testid="lead-select-website-state"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {WEBSITE_STATES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="sm:col-span-2">
              <Label>Konkretes Problem</Label>
              <Textarea data-testid="lead-input-problem" value={form.problem}
                onChange={(e) => set("problem", e.target.value)} rows={2}
                placeholder="Keine Website, veraltet, nicht mobil …" />
            </div>
            <div className="sm:col-span-2">
              <Label>Individueller Gesprächseinstieg</Label>
              <Textarea data-testid="lead-input-pitch" value={form.pitch}
                onChange={(e) => set("pitch", e.target.value)} rows={2}
                placeholder="Persönlicher Aufhänger fürs Telefonat …" />
            </div>
            <div className="sm:col-span-2">
              <Label>Notizen</Label>
              <Textarea data-testid="lead-input-notes" value={form.notes}
                onChange={(e) => set("notes", e.target.value)} rows={2} />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>Abbrechen</Button>
            <Button type="submit" data-testid="lead-submit-btn" disabled={saving}>
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Lead speichern
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
